import { useWebSocket } from "@/composables/useWebSocket";
import type { WSEventPayload } from "@shared/types";
import { defineStore } from "pinia";
import { ref, watch } from "vue";

export interface ScrapingProgressMessage {
    jobId: string;
    type: "job_started" | "navigating" | "page_loaded" | "extracting" | "round_complete" | "job_complete" | "job_failed";
    message: string;
    details?: {
        currentRound?: number;
        totalRounds?: number;
        vehicleIndex?: number;
        totalVehicles?: number;
        vehicleTitle?: string;
        vehicleFin?: string;
        attemptNumber?: number;
        maxAttempts?: number;
        resultsFound?: number;
        brand?: string;
        model?: string;
        fin?: string;
        jobsTotal?: number;
        jobIndex?: number;
    };
    timestamp: string;
}

const STORAGE_KEY = "scrapingProgress_currentMessage";
const STORAGE_ACTIVE_KEY = "scrapingProgress_isActive";

export const useScrapingProgressStore = defineStore("scrapingProgress", () => {
    // Restore from localStorage on init
    const storedMessage = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    const storedIsActive = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_ACTIVE_KEY) : null;
    
    const currentMessage = ref<ScrapingProgressMessage | null>(
        storedMessage ? JSON.parse(storedMessage) : null
    );
    const isActive = ref(storedIsActive === 'true');
    const messageHistory = ref<ScrapingProgressMessage[]>([]);

    // Auto-hide job_complete/job_failed after 5 seconds (restore this on page load if needed)
    if (currentMessage.value?.type === "job_complete" || currentMessage.value?.type === "job_failed") {
        if (isActive.value) {
            setTimeout(() => {
                isActive.value = false;
                currentMessage.value = null;
                if (typeof window !== 'undefined') {
                    localStorage.removeItem(STORAGE_KEY);
                    localStorage.removeItem(STORAGE_ACTIVE_KEY);
                }
            }, 5000);
        }
    }

    // Save to localStorage whenever state changes
    watch(currentMessage, (newVal) => {
        if (typeof window !== 'undefined') {
            if (newVal) {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(newVal));
            } else {
                localStorage.removeItem(STORAGE_KEY);
            }
        }
    });

    watch(isActive, (newVal) => {
        if (typeof window !== 'undefined') {
            if (newVal) {
                localStorage.setItem(STORAGE_ACTIVE_KEY, 'true');
            } else {
                localStorage.removeItem(STORAGE_ACTIVE_KEY);
            }
        }
    });

    const { on } = useWebSocket();

    // Listen to scraping progress events
    on("scraping:progress", (payload: WSEventPayload["scraping:progress"]) => {
            // IMPORTANT: Convert final round_complete to job_complete for UI consistency
            // When it's the last round (currentRound === totalRounds), treat it as job_complete
            if (payload.type === "round_complete" && 
                payload.details?.currentRound && 
                payload.details?.totalRounds &&
                payload.details.currentRound === payload.details.totalRounds) {
                
                // Transform to job_complete and update message
                payload.type = "job_complete";
                payload.message = `Alle ${payload.details.totalRounds} Runden abgeschlossen - Scraping fertig`;
            }
            
            currentMessage.value = payload;
            isActive.value = true;

            // Add to history (keep last 50 messages)
            messageHistory.value.unshift(payload);
            if (messageHistory.value.length > 50) {
                messageHistory.value = messageHistory.value.slice(0, 50);
            }

            // Auto-hide after job completion/failure (5 seconds)
            if (payload.type === "job_complete" || payload.type === "job_failed") {
                setTimeout(() => {
                    if (currentMessage.value?.jobId === payload.jobId) {
                        isActive.value = false;
                        currentMessage.value = null;
                        if (typeof window !== 'undefined') {
                            localStorage.removeItem(STORAGE_KEY);
                            localStorage.removeItem(STORAGE_ACTIVE_KEY);
                        }
                    }
                }, 5000);
            }
        });

    const clearHistory = () => {
        messageHistory.value = [];
    };

    const hide = () => {
        isActive.value = false;
        currentMessage.value = null;
    };

    return {
        currentMessage,
        isActive,
        messageHistory,
        clearHistory,
        hide,
    };
});
